import React, { useState, useEffect } from 'react';
import { generateAimSummary } from '../../services/geminiService';
import { useAimKpis, useAimRun } from './lib/aimQueries';
import { SkeletonAlert } from '../shared/Skeleton';

// Narrative comparison card shown right under the AI Monitor page header.
// Renders only when the user has explicitly picked a "Compare to" run. With
// Compare To set to "— None —" the card stays hidden — a delta narrative
// without an explicit baseline is misleading (markup #103).
export default function AimWhatChangedCard({ clientId, runId, compareRunId }) {
  const [dismissed, setDismissed] = useState(false);
  const [summaryText, setSummaryText] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const { data: kpiResp } = useAimKpis(clientId, runId, compareRunId || null);
  const kpis = kpiResp?.current || null;
  const prevKpis = kpiResp?.previous || null;

  const { data: run } = useAimRun(clientId, runId);
  const { data: compareRun } = useAimRun(clientId, compareRunId || null);

  // Reset dismissed state whenever the comparison context changes — a new
  // run selection deserves a fresh look at "what changed".
  useEffect(() => {
    setDismissed(false);
  }, [runId, compareRunId]);

  useEffect(() => {
    if (!compareRunId || !kpis || !prevKpis || !clientId || !runId) {
      setSummaryText(null);
      return undefined;
    }
    let cancelled = false;
    setSummaryLoading(true);
    generateAimSummary({
      clientId,
      runId,
      compareRunId,
      kpis,
      prevKpis,
      runDate: run?.run_date,
      compareRunDate: compareRun?.run_date,
    })
      .then((res) => {
        if (cancelled) return;
        setSummaryText(res.success ? res.data : null);
        setSummaryLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSummaryText(null);
        setSummaryLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId, runId, compareRunId, kpis, prevKpis, run, compareRun]);

  if (!clientId || !runId || !compareRunId || dismissed) return null;
  // Shimmer skeleton banner while the summary generates — same treatment as the
  // page skeleton, instead of the inline spinner.
  if (summaryLoading && !summaryText) return <SkeletonAlert lines={2} />;
  if (!summaryText) return null;

  return (
    <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">What Changed</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{summaryText}</p>
        </div>
      </div>
    </div>
  );
}
